// Rendu générique d'un modèle de formulaire d'inspection (JSON, G2). Le composant ne connaît ni le serveur ni
// le stockage : il reçoit un brouillon sérialisable et notifie chaque modification par un nouveau brouillon,
// que l'écran conserve chiffré sur l'appareil et envoie au serveur (directement ou par la file hors connexion).
// Chaque section indique ses réponses obligatoires restantes ; les erreurs du contrôle local et celles du
// serveur sont affichées au champ concerné.

import { useId, useState } from 'react';
import { estPhotoLocale, instancesSection, modifierBrouillon, type Brouillon, type Champ, type ContexteFormulaire, type ErreurReponse, type ModeleFormulaire, type Section, type Valeur } from './modele';

/** Décompte des photos jointes : sur le serveur, ou conservées chiffrées sur l'appareil en attendant le réseau. */
export function libellePhotos(ids: readonly string[]): string {
  if (ids.length === 0) return 'Aucune photo jointe.';
  const locales = ids.filter(estPhotoLocale).length;
  const pluriel = ids.length > 1 ? 's' : '';
  const base = `${ids.length} photo${pluriel} jointe${pluriel}`;
  if (locales === 0) return `${base}, chiffrée${pluriel} sur le serveur.`;
  if (locales === ids.length) return `${base}, conservée${pluriel} chiffrée${pluriel} sur cet appareil, envoyée${pluriel} au retour du réseau.`;
  return `${base}, dont ${locales} conservée${locales > 1 ? 's' : ''} sur cet appareil, envoyée${locales > 1 ? 's' : ''} au retour du réseau.`;
}

export interface ProprietesFormulaire {
  modele: ModeleFormulaire;
  contexte: ContexteFormulaire;
  brouillon: Brouillon;
  surChangement: (b: Brouillon) => void;
  lectureSeule?: boolean;
  erreurs?: ErreurReponse[];
  libellesProduits?: Record<string, string>;
  /** Téléverse une photo et renvoie l'identifiant de la pièce ; absent en lecture seule ou sans réseau. */
  televerserPhoto?: (fichier: File) => Promise<string>;
  /** Motif affiché à la place du bouton d'ajout de photo quand le téléversement est impossible. */
  motifSansPhoto?: string;
}

const vide = (v: Valeur | undefined) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

/** Réponses obligatoires renseignées et attendues pour une section (toutes instances confondues). */
export function avancementSection(section: Section, contexte: ContexteFormulaire, b: Brouillon): { faites: number; attendues: number } {
  let faites = 0;
  let attendues = 0;
  for (const inst of instancesSection(section, contexte)) {
    for (const c of section.champs.filter((x) => x.obligatoire)) {
      attendues++;
      if (!vide(b.reponses[section.code]?.[inst.cle]?.[c.code])) faites++;
    }
  }
  return { faites, attendues };
}

export function avancementFormulaire(modele: ModeleFormulaire, contexte: ContexteFormulaire, b: Brouillon) {
  return modele.sections.reduce(
    (t, s) => {
      const a = avancementSection(s, contexte, b);
      return { faites: t.faites + a.faites, attendues: t.attendues + a.attendues };
    },
    { faites: 0, attendues: 0 },
  );
}

function ChampSaisie({
  id,
  champ,
  valeur,
  lectureSeule,
  erreur,
  surValeur,
  televerserPhoto,
  motifSansPhoto,
}: {
  id: string;
  champ: Champ;
  valeur: Valeur | undefined;
  lectureSeule: boolean;
  erreur: string | undefined;
  surValeur: (v: Valeur) => void;
  televerserPhoto?: (fichier: File) => Promise<string>;
  motifSansPhoto?: string;
}) {
  const [envoi, setEnvoi] = useState<{ enCours: boolean; erreur: string | null }>({ enCours: false, erreur: null });
  const decrit = [erreur ? `${id}-erreur` : null, champ.aide ? `${id}-aide` : null].filter(Boolean).join(' ') || undefined;
  const communs = { id, disabled: lectureSeule, 'aria-invalid': erreur ? true : undefined, 'aria-describedby': decrit, 'aria-required': champ.obligatoire || undefined };
  let controle;
  switch (champ.type) {
    case 'texte':
      controle = <input {...communs} type="text" maxLength={300} value={typeof valeur === 'string' ? valeur : ''} onChange={(e) => surValeur(e.target.value)} />;
      break;
    case 'texte_long':
      controle = <textarea {...communs} rows={3} maxLength={5000} value={typeof valeur === 'string' ? valeur : ''} onChange={(e) => surValeur(e.target.value)} />;
      break;
    case 'nombre':
      controle = (
        <input
          {...communs}
          type="number"
          inputMode="numeric"
          step={1}
          min={champ.min}
          max={champ.max}
          value={typeof valeur === 'number' ? valeur : ''}
          onChange={(e) => surValeur(e.target.value === '' ? null : Number(e.target.value))}
        />
      );
      break;
    case 'decimal':
      // Les quantités restent des chaînes décimales : aucune conversion en nombre flottant.
      controle = <input {...communs} type="text" inputMode="decimal" autoComplete="off" value={typeof valeur === 'string' ? valeur : ''} onChange={(e) => surValeur(e.target.value.trim() === '' ? null : e.target.value.trim())} />;
      break;
    case 'booleen':
      controle = (
        <div className="segments" role="radiogroup" aria-labelledby={`${id}-libelle`} aria-describedby={decrit}>
          {[
            { v: true, l: 'Oui' },
            { v: false, l: 'Non' },
          ].map((o) => (
            <button key={o.l} type="button" role="radio" aria-checked={valeur === o.v} disabled={lectureSeule} onClick={() => surValeur(valeur === o.v ? null : o.v)}>
              {o.l}
            </button>
          ))}
        </div>
      );
      break;
    case 'choix':
      controle = (
        <select {...communs} value={typeof valeur === 'string' ? valeur : ''} onChange={(e) => surValeur(e.target.value || null)}>
          <option value="">Non renseigné</option>
          {(champ.choix ?? []).map((c) => (
            <option key={c.valeur} value={c.valeur}>
              {c.libelle}
            </option>
          ))}
        </select>
      );
      break;
    case 'date':
      controle = <input {...communs} type="date" value={typeof valeur === 'string' ? valeur : ''} onChange={(e) => surValeur(e.target.value || null)} />;
      break;
    case 'photo': {
      const photos = Array.isArray(valeur) ? valeur : [];
      controle = (
        <div className="champ-photo">
          <span className="attenue">{libellePhotos(photos)}</span>
          {!lectureSeule && televerserPhoto && (
            <input
              {...communs}
              type="file"
              accept="image/jpeg,image/png"
              capture="environment"
              disabled={envoi.enCours}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (!f) return;
                setEnvoi({ enCours: true, erreur: null });
                televerserPhoto(f)
                  .then((idPiece) => {
                    surValeur([...photos, idPiece]);
                    setEnvoi({ enCours: false, erreur: null });
                  })
                  .catch((err: unknown) => setEnvoi({ enCours: false, erreur: err instanceof Error ? err.message : 'Envoi refusé' }));
              }}
            />
          )}
          {!lectureSeule && !televerserPhoto && motifSansPhoto && <span className="aide">{motifSansPhoto}</span>}
          {envoi.enCours && (
            <span className="attenue" role="status">
              Envoi de la photo en cours
            </span>
          )}
          {envoi.erreur && <span className="erreur-champ">{envoi.erreur}</span>}
        </div>
      );
      break;
    }
  }
  const libelle = (
    <>
      {champ.libelle}
      {champ.unite ? ` (${champ.unite})` : ''}
      {champ.obligatoire ? <span aria-hidden="true"> *</span> : null}
    </>
  );
  return (
    <div className="champ">
      {champ.type === 'booleen' ? <span id={`${id}-libelle`} className="libelle-champ">{libelle}</span> : <label htmlFor={id}>{libelle}</label>}
      {controle}
      {champ.aide && (
        <span id={`${id}-aide`} className="aide">
          {champ.aide}
        </span>
      )}
      {erreur && (
        <span id={`${id}-erreur`} className="erreur-champ">
          {erreur}
        </span>
      )}
    </div>
  );
}

export function FormulaireGenerique({ modele, contexte, brouillon, surChangement, lectureSeule = false, erreurs = [], libellesProduits = {}, televerserPhoto, motifSansPhoto }: ProprietesFormulaire) {
  const base = useId();
  const erreurDe = (chemin: string) => erreurs.find((e) => e.chemin === chemin)?.message;
  return (
    <div className="formulaire-inspection">
      {modele.sections.map((section, rang) => {
        const instances = instancesSection(section, contexte);
        const { faites, attendues } = avancementSection(section, contexte, brouillon);
        const complete = attendues === 0 || faites === attendues;
        return (
          <fieldset key={section.code} id={`section-${section.code}`} className="carte-bloc section-formulaire">
            <legend className="visuellement-cache">{section.titre}</legend>
            <div className="carte-bloc-tete">
              <h2 aria-hidden="true">
                <span className="attenue">{rang + 1}.</span> {section.titre}
              </h2>
              {attendues > 0 && <span className={complete ? 'etiquette' : 'etiquette ambre'}>{complete ? 'Réponses obligatoires complètes' : `${attendues - faites} réponse${attendues - faites > 1 ? 's' : ''} obligatoire${attendues - faites > 1 ? 's' : ''} à saisir`}</span>}
            </div>
            {instances.length === 0 && <p className="attenue">Aucun élément à relever pour cette installation (aucun réservoir ou produit enregistré).</p>}
            {instances.map((inst) => (
              <div key={inst.cle} className="instance-formulaire">
                {section.repetition !== 'aucune' && <h3>{section.repetition === 'produits' ? (libellesProduits[inst.cle] ?? inst.libelle) : inst.libelle}</h3>}
                <div className="formulaire-ligne">
                  {section.champs.map((champ) => {
                    const chemin = `${section.code}.${inst.cle}.${champ.code}`;
                    return (
                      <ChampSaisie
                        key={champ.code}
                        id={`${base}-${chemin}`}
                        champ={champ}
                        valeur={brouillon.reponses[section.code]?.[inst.cle]?.[champ.code]}
                        lectureSeule={lectureSeule}
                        erreur={erreurDe(chemin)}
                        surValeur={(v) => surChangement(modifierBrouillon(brouillon, section.code, inst.cle, champ.code, v))}
                        televerserPhoto={televerserPhoto}
                        motifSansPhoto={motifSansPhoto}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </fieldset>
        );
      })}
    </div>
  );
}
